import { useState } from 'react';

import { CostTrackingResponse, Report } from '@/app/models/costTracking';
import { formatDateMonthYear } from '@/app/utils/dateUtils';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import TextButton from '@/components/common/TextButton';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Box, Menu, MenuItem, TableCell, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import getReportValue from '@/app/utils/reportValueFormatter';

interface RubricTableProps {
  setDrawerEdit: CallableFunction;
  setSelectedItem: (id: Report[]) => void;
  data: CostTrackingResponse[] | undefined;
}

export default function RubricTable({ setSelectedItem, setDrawerEdit, data }: RubricTableProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation(['costTracking', 'common']);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrowersEdit = () => {
    setDrawerEdit(true);
    setAnchorEl(null);
  };

  const columns: Column[] = [
    { key: 'month', label: t('common:month'), width: theme.spacing(30) },
    { key: 'Baseline', label: t('baseline'), width: theme.spacing(30) },
    { key: 'Replanned', label: t('replanned'), width: theme.spacing(30) },
    { key: 'Done', label: t('fulfilled') },
    { key: 'actions', label: t('common:actions'), align: 'center', width: theme.spacing(20) },
  ];

  const renderRow = (item: CostTrackingResponse) => {
    return (
      <>
        <TableCell sx={bodyCellSx}>{formatDateMonthYear(item.period)}</TableCell>
        <TableCell sx={{ ...bodyCellSx }}>{getReportValue(item.reports, 'BASELINE')}</TableCell>
        <TableCell sx={{ ...bodyCellSx }}>{getReportValue(item.reports, 'REPLANNED')}</TableCell>
        <TableCell sx={{ ...bodyCellSx }}>{getReportValue(item.reports, 'FULFILLED')}</TableCell>
        <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
          <TextButton
            sx={{ ml: 'auto' }}
            text={`+ ${t('common:comment')}`}
            onClick={() => {
              setSelectedItem(item.reports);
              handleDrowersEdit();
            }}
          />
        </TableCell>
      </>
    );
  };

  const getRowKey = (project: CostTrackingResponse) => project.period as string;

  return (
    <>
      <DataTable
        columns={columns}
        data={data || []}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
        size="small"
        maxHeight={theme.spacing(60)}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleDrowersEdit()}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ListAltIcon />
            {t('common:edit.detail')}
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
}
